"use client";

import { Select as SelectPrimitive } from "radix-ui";
import { RiArrowDownSLine } from "react-icons/ri";
import { type VariantProps } from "tailwind-variants";

import { tv } from "@/lib/tailwind-variants";
import { cn } from "@/lib/utils";

const variants = tv({
  slots: {
    base: "flex h-8.5 w-full items-center justify-between gap-1 rounded-sm border bg-white p-2 text-Medium leading-tight text-gray-700 placeholder:text-gray-300 invalid:border-error-500 focus-visible:ring-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100",
    viewport: "flex items-center gap-2",
    item: "grid size-6 flex-none cursor-default place-content-center rounded-full border border-gray-200 bg-gray-100 text-Large leading-tight text-gray-400 outline-hidden select-none hover:border-primary hover:bg-primary hover:text-white focus:border-primary focus:bg-primary focus:text-white data-disabled:pointer-events-none data-disabled:opacity-50",
  },
  variants: {
    invalid: {
      false: {
        base: "border-gray-300 focus-visible:border-info-500 focus-visible:ring-info-100",
      },
      true: {
        base: "border-error-500 focus-visible:border-error-500 focus-visible:ring-error-100",
      },
    },
  },
  defaultVariants: {
    invalid: false,
  },
});

const { base, viewport, item } = variants();

const NumberSelect = SelectPrimitive.Root;

const NumberSelectValue = SelectPrimitive.Value;

const NumberSelectTrigger = ({
  invalid = false,
  asChild = false,
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof variants>) => {
  if (asChild) {
    return (
      <SelectPrimitive.Trigger asChild {...props}>
        {children}
      </SelectPrimitive.Trigger>
    );
  }

  return (
    <SelectPrimitive.Trigger
      className={cn(base({ invalid }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <RiArrowDownSLine size={20} className="flex-none text-gray-500" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};

const NumberSelectContent = ({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) => {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          "relative min-w-36 rounded-sm border border-gray-200 bg-white p-4 text-gray-700 shadow-info-popup data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "max-h-(--radix-select-content-available-height) data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        {children}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const NumberSelectViewport = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Viewport>) => {
  return (
    <SelectPrimitive.Viewport className={cn(viewport(), className)} {...props}>
      {children}
    </SelectPrimitive.Viewport>
  );
};

const NumberSelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => {
  return (
    <SelectPrimitive.Item className={cn(item(), className)} {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};

export {
  NumberSelect,
  NumberSelectValue,
  NumberSelectTrigger,
  NumberSelectContent,
  NumberSelectViewport,
  NumberSelectItem,
};
